import {
    baseUrl,
    clearEntryCache,
    download,
    getEntryUrl,
    getKmsData,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { UnisphereRuntimeVisualInBody, useUnispherePubSub } from "@kaltura/mediaspace-shared-unisphere";
import { Button, Tooltip } from "@kaltura/ds-react-components";
import { GenAI24Icon } from "@kaltura/ds-react-icons";
import { useEffect, useMemo, useState } from "react";
import { UnisphereMessage } from "@unisphere/runtime";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";
import { styled } from "@mui/material";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { MediaPageComponentExtraProps } from "../../../../MediaPageComponentExtraProps";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    AIConsentRuntime,
    AIConsentRuntimeName,
    AIConsentRuntimeSettings,
    ApplicationRuntime,
    ApplicationRuntimeName,
    ApplicationRuntimeSettings,
    UnavailabilityReason,
    widgetName,
} from "@unisphere/content-lab-types";

const CONTENT_LAB_PUB_SUB_EVENTS_NAME = "unisphere.event.module.content-lab.message-host-app";

// Enum to define the types of ContentLab widgets
export enum ContentLabWidgetType {
    CLIPS = "clips", // when selected create clips feature will be automatically selected
    QUIZ = "quiz", // when selected create quiz feature will be automatically selected
    SUMMARY = "summary", // when selected create summary feature will be automatically selected
    METADATA_ENRICHMENT = "metadata", // when selected metadata enrichment feature will be automatically selected
    CAPTIONS = "captions", // when selected captions/translations ordering feature will be available
}

export interface ContentLabCtaProps extends MediaPageComponentExtraProps {
    // the primary entry: the VOD entry for VOD media, or the live entry for live media
    entry: KalturaMediaEntry;
    // the context entry id - when dealing with a live entry, this is the live id. for other media types, an empty string.
    sessionContextId: string;
    // id of the VOD recording, only present for live entries that have a recording
    recordingEntryId?: string;
    // uiConf id for the preview player inside the content lab
    playerId: string;
    // uses user ks
    ks: string;
    buttonLabel?: string;
    // is "load thumbnail with KS" flag enabled for the partner
    loadThumbnailWithKS?: boolean;
    // Error text coming from backend checks
    errorText?: string;
    // the type of the content lab widgets, if omitted the default value is CLIPS for backward compatibility
    widgets?: ContentLabWidgetType[];
    // Reach module config passed to the CL widget for ordering captions/translations
    reachConfig?: Record<string, unknown>;
    // URL template for the captions editor; {assetID_placeholder} is replaced with the caption asset ID
    editCaptionUrl?: string;
}

const enum PostSaveActions {
    EDIT_CLIP = "edit",
    DOWNLOAD_CLIP = "download",
    NAVIGATE_TO_MEDIA = "media",
    EDIT_QUIZ = "editQuiz",
    DOWNLOAD_QUIZ = "downloadQuiz",
    EDIT_PLAYLIST = "editPlaylist",
    NAVIGATE_TO_PLAYLIST = "playlist",
    ADD_CAPTIONS = "addCaptions",
    EDIT_CAPTIONS = "editCaptions",
    UPDATE_METADATA = "updateMetadata",
}

const postSaveActionsByWidget = {
    [ContentLabWidgetType.CLIPS]: [
        PostSaveActions.EDIT_CLIP,
        PostSaveActions.DOWNLOAD_CLIP,
        PostSaveActions.NAVIGATE_TO_MEDIA,
        PostSaveActions.NAVIGATE_TO_PLAYLIST,
        PostSaveActions.EDIT_PLAYLIST,
    ],
    [ContentLabWidgetType.QUIZ]: [
        PostSaveActions.EDIT_QUIZ,
        PostSaveActions.DOWNLOAD_QUIZ,
        PostSaveActions.NAVIGATE_TO_MEDIA,
    ],
    [ContentLabWidgetType.SUMMARY]: [],
    [ContentLabWidgetType.METADATA_ENRICHMENT]: [],
    [ContentLabWidgetType.CAPTIONS]: [PostSaveActions.EDIT_CAPTIONS],
};

// if the widget is not specified, the default post save actions will be the union of the post save actions of the clips and quiz widgets
const allPostSaveActions = Array.from(new Set(Object.values(postSaveActionsByWidget).flat()));

// The "widgets" prop is memoized, so its default value must be constant reference as well
const defaultWidgets = [ContentLabWidgetType.CLIPS];

/**
 * ContentLabCta component renders a ContentLab widget and handles its events.
 * It subscribes to Unisphere PubSub events and performs actions based on the event data.
 */
export const ContentLabCta = ({
    ks,
    playerId,
    entry,
    sessionContextId,
    recordingEntryId,
    loadThumbnailWithKS = false,
    errorText,
    buttonLabel = "",
    widgets = defaultWidgets,
    reachConfig,
    editCaptionUrl,
    onRequestEntryReload,
}: ContentLabCtaProps) => {
    const theme = useTheme();
    const shouldShowContentLab = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.sm));
    const postSaveActions = useMemo(
        () =>
            !widgets || !widgets.length
                ? allPostSaveActions
                : Array.from(new Set(widgets?.flatMap((widget) => postSaveActionsByWidget[widget]))),
        [widgets]
    );

    const {
        application: { partnerId, serviceUrl },
        analytics,
    } = useConfig();

    // Memoized settings for the contentLab widget
    const contentLabSettings = useMemo(
        (): ApplicationRuntimeSettings => ({
            _schemaVersion: "1",
            ks,
            pid: String(partnerId),
            uiconfId: playerId,
            analyticsServerURI: analytics.serviceUrl,
            hostAppName: analytics.appType,
            hostAppVersion: version,
            kalturaServerURI: serviceUrl,
            clipsOverride: "",
            loadThumbnailWithKS,
            widget: widgets?.join(","),
            postSaveActions: postSaveActions.join(","),
            hideAddCaptionsCTA: true,
            hostedInKalturaProduct: true,
            reachConfig,
        }),
        [ks, partnerId, playerId, analytics, serviceUrl, widgets, postSaveActions, loadThumbnailWithKS, reachConfig]
    );

    const aiConsentSettings = useMemo(
        () =>
            ({
                _schemaVersion: "1",
                ks,
                pid: String(partnerId),
                hostApp: "kms",
                kaltura: {
                    analyticsServerURI: analytics.serviceUrl,
                    hostAppName: analytics.appType,
                    hostAppVersion: version,
                },
                // Note: the AIConsentRuntimeSettings type is buggy at the moment, so we need to use loose type check
            }) as AIConsentRuntimeSettings,
        [ks, partnerId, analytics]
    );

    const [contentLabRuntime, setContentLabRuntime] = useState<ApplicationRuntime>();

    const [isPartnerCheckReady, setIsPartnerCheckReady] = useState(false);
    const [isEntryCheckReady, setIsEntryCheckReady] = useState(false);
    const [partnerCheckError, setPartnerCheckError] = useState("");
    const [entryCheckError, setEntryCheckError] = useState("");
    const isReady = !!contentLabRuntime && isPartnerCheckReady && isEntryCheckReady;
    const error = errorText || partnerCheckError || entryCheckError;
    const loading = !isReady && !error;

    // Closes the contentLab widget
    const closeContentLabWidget = () => (contentLabRuntime as any)?.closeWidget();

    const downloadQuizQuestions = async (entry: KalturaMediaEntry) => {
        const response = await getKmsData(
            `${baseUrl}/kalturaai/quiz/get-questions?entryId=${encodeURIComponent(entry.id)}`
        );
        if (response.success) {
            download(response.questionsDownloadUrl, entry.name);
        }
    };

    // Handles events from the contentLab widget
    useUnispherePubSub(
        CONTENT_LAB_PUB_SUB_EVENTS_NAME,
        (data: UnisphereMessage) => {
            const { action, entry, caption } = data.payload;
            switch (action) {
                case PostSaveActions.NAVIGATE_TO_MEDIA:
                    closeContentLabWidget();
                    document.location.href = getEntryUrl(entry.id, undefined, entry.name);
                    break;
                case PostSaveActions.DOWNLOAD_CLIP:
                    download(`${entry.downloadUrl}?ks=${ks}`, entry.name);
                    break;
                case PostSaveActions.EDIT_CLIP:
                    closeContentLabWidget();
                    document.location.href = `${baseUrl}/editor/index/launch-editor?entryId=${entry.id}#kea-anchor`;
                    break;
                case PostSaveActions.EDIT_QUIZ:
                    closeContentLabWidget();
                    document.location.href = `${baseUrl}/editor/index/launch-editor?entryId=${entry.id}&tab=quiz`;
                    break;
                case PostSaveActions.DOWNLOAD_QUIZ:
                    downloadQuizQuestions(entry);
                    break;
                case PostSaveActions.EDIT_PLAYLIST:
                    document.location.href = `${baseUrl}/playlist/edit/${entry.id}`;
                    break;
                case PostSaveActions.NAVIGATE_TO_PLAYLIST:
                    document.location.href = `${baseUrl}/playlist/dedicated/${entry.id}`;
                    break;
                case PostSaveActions.ADD_CAPTIONS:
                    /*
                     * Not supported at the moment.
                     * The button should be disabled when there are no captions.
                     */
                    console.warn("Add Captions requested - shouldn't get to this point in UI!");
                    break;
                case PostSaveActions.EDIT_CAPTIONS:
                    closeContentLabWidget();
                    if (editCaptionUrl && caption?.id && entry?.id) {
                        document.location.href = editCaptionUrl
                            .replace("{assetID_placeholder}", caption.id)
                            .replace("{entryID_placeholder}", entry.id);
                    }
                    break;
                case PostSaveActions.UPDATE_METADATA:
                    closeContentLabWidget();
                    clearEntryCache(entry.id).then(() => {
                        onRequestEntryReload?.();
                    });
                    break;
                default:
                    break;
            }
        }
    );

    useEffect(() => {
        if (!contentLabRuntime) {
            return;
        }

        setIsPartnerCheckReady(false);
        setPartnerCheckError("");
        contentLabRuntime.partnerChecks.onChanges(
            (data) => {
                console.debug("CL partner check changes:", data);
                switch (data.status) {
                    case "loaded":
                        if (data.isAvailable) {
                            setIsPartnerCheckReady(true);
                        }
                        else {
                            setPartnerCheckError(
                                translate("There are configuration issues, please reach out to your site administrator")
                            );
                        }
                        break;
                    case "error":
                        setPartnerCheckError(translate("Something went wrong, try again later"));
                        console.debug("CL partner check error:", data.error);
                        break;
                }
            },
            { replayLastValue: true }
        );
    }, [contentLabRuntime]);

    useEffect(() => {
        if (!contentLabRuntime) {
            return;
        }

        setIsEntryCheckReady(false);
        setEntryCheckError("");
        contentLabRuntime.isEntryRelevant(entry).then(
            (result) => {
                console.debug("CL isEntryRelevant result:", result);
                if (result.canUse) {
                    setIsEntryCheckReady(true);
                }
                else {
                    switch (result.rejectionReason) {
                        case UnavailabilityReason.ENTRY_DURATION_LESS_THAN_MIN:
                            setEntryCheckError(
                                translate("To create with AI, video duration must be at least 60 seconds")
                            );
                            break;
                        case UnavailabilityReason.ENTRY_NOT_READY:
                            setEntryCheckError(translate("To create with AI, this entry must be ready"));
                            break;
                        case UnavailabilityReason.ENTRY_TYPE_NOT_SUPPORTED:
                            setEntryCheckError(
                                translate("To create with AI, this entry must be of type video or audio")
                            );
                            break;
                        case UnavailabilityReason.AI_CONSENT:
                            setEntryCheckError(
                                translate("To create with AI, you must accept the terms and conditions")
                            );
                            break;
                        default:
                            setEntryCheckError(translate("Something went wrong, try again later"));
                            break;
                    }
                }
            },
            (error) => {
                console.debug("CL isEntryRelevant error:", error);
                setEntryCheckError(translate("Something went wrong, try again later"));
            }
        );
    }, [contentLabRuntime, entry]);

    const sendButtonAnalytics = useButtonAnalytics();
    const handleClick = () => {
        if (!contentLabRuntime || !isReady || error) {
            return;
        }

        pausePlayers();

        sendButtonAnalytics("Open content lab", ButtonClickAnalyticsType.OPEN, entry.id, "KalturaAi");

        contentLabRuntime.openApplication({
            type: "entry",
            entryId: sessionContextId ? recordingEntryId ?? "" : entry.id,
            eventSessionContextId: sessionContextId,
        });
    };

    const pausePlayers = () => {
        const allPlayersObject = (window as any).KalturaPlayer?.getPlayers?.();
        const allPlayers = allPlayersObject ? Object.values(allPlayersObject) : [];
        allPlayers?.forEach((player: any) => player.pause?.());
    };

    // Render the ContentLab widget component only on desktop layout
    return (
        <>
            {shouldShowContentLab && (
                <Tooltip title={loading ? translate("Loading the Content Lab...") : error}>
                    <StyledButton startIcon={<GenAI24Icon />} onClick={handleClick} loading={loading}>
                        {buttonLabel || translate("Create with Content Lab")}
                    </StyledButton>
                </Tooltip>
            )}

            {/* No need to load the widgets if the backend check failed */}
            {!errorText && (
                <>
                    <UnisphereRuntimeVisualInBody<AIConsentRuntime>
                        widgetName={widgetName}
                        runtimeName={AIConsentRuntimeName}
                        runtimeSettings={aiConsentSettings}
                        visualType={"announcement"}
                    />

                    <UnisphereRuntimeVisualInBody<ApplicationRuntime>
                        widgetName={widgetName}
                        runtimeName={ApplicationRuntimeName}
                        runtimeSettings={contentLabSettings}
                        visualType={"drawer"}
                        onRuntimeAvailable={setContentLabRuntime}
                    />
                </>
            )}
        </>
    );
};

const StyledButton = styled(Button)({
    // Force minimal button width for the loading state not to look weird
    minWidth: 130,
});

export default ContentLabCta;
